import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "./api";
import { handleResponseStatus, updateIdField, updateItem } from "./utils";

const initialState = {
    items: [],
    fetchStatus: "success",
    fetchByIdStatus: "idle",
    createStatus: "idle",
    manageOwnerStatus: "idle",
    manageUserStatus: "idle",
    updateStatus: "idle",
    deleteStatus: "idle",
    pagination: {},
    error: null
};

export const fetchRestaurants = createAsyncThunk('restaurant/fetchRestaurants', async ({ page = 1, limit = 10 } = {}) => {
    try {
        const response = await api.get(`/sdg/restaurant?page=${page}&limit=${limit}`);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createRestaurant = createAsyncThunk('restaurant/createRestaurant', async (data) => {
    try {
        const response = await api.post(`/sdg/restaurant`, data, {
            useMultipart: true,
        });
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const deleteRestaurant = createAsyncThunk('restaurant/deleteRestaurant', async (id) => {
    try {
        const response = await api.delete(`/sdg/restaurant/${id}`);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateRestaurant = createAsyncThunk('restaurant/updateRestaurant', async (data) => {
    try {
        const response = await api.patch(`/sdg/restaurant/${data.id}`, data, {
            useMultipart: true,
        });
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchRestaurantById = createAsyncThunk('restaurant/fetchRestaurantById', async (id) => {
    try {
        const response = await api.get(`/sdg/restaurant/${id}`);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});


export const manageRestaurantOwner = createAsyncThunk('restaurant/manageRestaurantOwner', async (data) => {
    try {
        const response = await api.post(`/sdg/restaurant/manageowner`, data);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});
export const manageRestaurantUser = createAsyncThunk('restaurant/manageRestaurantUser', async (data) => {
    try {
        const response = await api.post(`/sdg/restaurant/manageuser`, data);
        return { ...response };
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

const restaurantSlice = createSlice({
    name: "restaurant",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetManageUserStatus(state) {
            state.manageUserStatus = 'idle';
        },
        resetManageOwnerStatus(state) {
            state.manageOwnerStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRestaurants.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchRestaurants.fulfilled, (state, action) => {
                const { fetchStatus, error } = handleResponseStatus(action.payload, 'fetchStatus');
                const { data, ...pagination } = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchRestaurants.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(fetchRestaurantById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchRestaurantById.fulfilled, (state, action) => {
                const { fetchByIdStatus, error } = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchRestaurantById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(createRestaurant.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createRestaurant.fulfilled, (state, action) => {
                const { createStatus, error } = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createRestaurant.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(manageRestaurantOwner.pending, (state) => {
                state.manageOwnerStatus = 'loading';
            })
            .addCase(manageRestaurantOwner.fulfilled, (state, action) => {
                const { manageOwnerStatus, error } = handleResponseStatus(action.payload, 'manageOwnerStatus');
                state.manageOwnerStatus = manageOwnerStatus;
                state.error = error;
            })
            .addCase(manageRestaurantOwner.rejected, (state, action) => {
                state.manageOwnerStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(manageRestaurantUser.pending, (state) => {
                state.manageUserStatus = 'loading';
            })
            .addCase(manageRestaurantUser.fulfilled, (state, action) => {
                const { manageUserStatus, error } = handleResponseStatus(action.payload, 'manageUserStatus');
                state.manageUserStatus = manageUserStatus;
                state.error = error;
            })
            .addCase(manageRestaurantUser.rejected, (state, action) => {
                state.manageUserStatus = 'failed';
                state.error = action.error.message;
            })

            .addCase(updateRestaurant.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateRestaurant.fulfilled, (state, action) => {
                const { updateStatus, error } = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateRestaurant.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(deleteRestaurant.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteRestaurant.fulfilled, (state, action) => {
                const { deleteStatus, error } = handleResponseStatus(action.payload, 'deleteStatus');
                state.deleteStatus = deleteStatus;
                state.items = state.items.filter((item) => item.id !== action.payload.data.data._id);
                state.error = error;
            })
            .addCase(deleteRestaurant.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const { resetUpdateStatus, resetDeleteStatus, resetCreateStatus, resetManageUserStatus, resetManageOwnerStatus } = restaurantSlice.actions;
export default restaurantSlice.reducer;
